import React from 'react';
import { motion } from 'framer-motion';
import { Check, Heart, Clock, Calendar, MessageCircle, Shield, IndianRupee, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useDemoModal } from '../context/DemoModalContext';

const Pricing = () => {
  const { openDemoModal } = useDemoModal();

  const whatsappNumber = "919538667790";
  const whatsappMessage = encodeURIComponent("Hi! I'm interested in getting a pricing quote for FourQ Learn tutoring services.");
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const benefits = [
    {
      icon: IndianRupee,
      title: 'Affordable Pricing',
      description: 'Quality education at prices that work for every family. No premium packages or hidden costs.',
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: Shield,
      title: 'No Advance Payment',
      description: 'Pay only for the classes attended. No upfront fees, no deposits, no commitments.',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: Clock,
      title: 'Pay Per Hour',
      description: 'Simple per-hour pricing. You pay only for the actual class time your child attends.',
      color: 'from-purple-400 to-pink-500'
    },
    {
      icon: Calendar,
      title: 'Flexible Scheduling',
      description: 'Cancel, postpone, or change class timings anytime. We understand life happens.',
      color: 'from-orange-400 to-red-500'
    }
  ];

  const features = [
    '1-on-1 personalized live sessions',
    'Experienced & verified tutors',
    'All subjects covered (Grade 3-12)',
    'Session recordings for revision',
    'Real-time progress tracking',
    'Interactive whiteboard & tools',
    'Regular parent-teacher updates',
    'Flexible rescheduling options'
  ];

  const subjects = [
    'Mathematics',
    'Science (Physics, Chemistry, Biology)',
    'English Language & Literature',
    'Social Studies',
    'Hindi',
    'Computer Science',
    'Economics',
    'Accountancy'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <motion.div
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center text-white"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Quality Education, Affordable Price
            </h1>
            <p className="text-xl text-cyan-100 mb-8">
              No hidden fees. No advance payments. Pay only for what you use.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-white text-cyan-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-xl shadow-xl hover:scale-105 transition-all duration-300"
                onClick={() => window.open(whatsappLink, '_blank')}
                data-testid="pricing-whatsapp-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Get Pricing Quote
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl backdrop-blur-sm transition-all duration-300"
                onClick={openDemoModal}
                data-testid="pricing-demo-btn"
              >
                Book Free Session
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Parents <span className="text-cyan-600">Love Us</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We believe quality education should be accessible to everyone
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 text-center"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Pricing */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Simple, Transparent <span className="text-cyan-600">Pricing</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We keep things simple. No confusing packages, no long-term contracts, no advance payments. 
                Just quality tutoring at a per-hour rate that fits your budget.
              </p>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-cyan-500">
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-2">Pay only for classes attended</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-5xl font-bold text-gray-900">Per Hour</span>
                  </div>
                  <p className="text-cyan-600 font-semibold mt-2">No hidden fees • No advance payment</p>
                </div>
                
                <div className="space-y-3">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 space-y-3">
                  <Button 
                    className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg"
                    onClick={() => window.open(whatsappLink, '_blank')}
                    data-testid="pricing-card-whatsapp-btn"
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Chat on WhatsApp for Quote
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 py-6 text-lg"
                    onClick={openDemoModal}
                    data-testid="pricing-card-demo-btn"
                  >
                    Book a Free Session First
                  </Button>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Cancel Anytime</h3>
                      <p className="text-gray-600">No penalties, no questions asked. Your flexibility matters.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Reschedule Freely</h3>
                      <p className="text-gray-600">Postpone or change class timings with just a message.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Student-First Approach</h3>
                      <p className="text-gray-600">We focus on learning outcomes, not billing cycles.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Quick Support</h3>
                      <p className="text-gray-600">Reach us on WhatsApp for instant pricing and queries.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              All Subjects Covered
            </h2>
            <p className="text-xl text-gray-600">For students from Grade 3 to 12</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {subjects.map((subject, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all"
              >
                <Check className="w-6 h-6 text-cyan-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-900">{subject}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-teal-600">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center text-white"
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Chat with us on WhatsApp for a personalized pricing quote, or book a free session to experience our teaching methodology.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-white text-cyan-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-xl shadow-xl hover:scale-105 transition-all duration-300"
                onClick={() => window.open(whatsappLink, '_blank')}
                data-testid="pricing-cta-whatsapp-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp Us
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl backdrop-blur-sm transition-all duration-300"
                onClick={openDemoModal}
                data-testid="pricing-cta-demo-btn"
              >
                Book Free Session
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How does the pricing work?</h3>
              <p className="text-gray-600">We charge on a per-hour basis for the classes your child attends. No packages, no advance payments. Contact us on WhatsApp for exact pricing based on your requirements.</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Can I try before committing?</h3>
              <p className="text-gray-600">Absolutely! Book a free session to experience our teaching methodology. No payment required for the demo session.</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What if I need to cancel or reschedule?</h3>
              <p className="text-gray-600">You can cancel, postpone, or change class timings anytime. Just let us know in advance, and we'll adjust accordingly. No penalties.</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Are there any hidden fees?</h3>
              <p className="text-gray-600">No hidden fees whatsoever. You pay only for the hours of tutoring your child receives. Everything is transparent and upfront.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
